import * as _ from "lodash";
import React, { useState } from "react";
import * as Models from "../../../Models";
import DocumentDataGrid from "../DataGrid/GroupedDataGrid";
import { CollapsibleCard } from "../../General/Card";
import {
    getItemGroupData,
    createChildRows,
    withExpander,
    withRemover,
} from "../../../Helpers/documentTable";
import { DocumentsCardFileUpload } from "../FileUpload";
import * as Modal from "react-modal";
import { canRemoveFile } from "../../../Helpers/documents";
import { useMarketAreaStore, userStore } from "../../../Store/userStore";
import { useSendDeleteDocumentRequest } from "../../../Api/azure";
import { toast } from "react-toastify";
import filterItemsStore from "../../../Store/filterItems";

interface IDocumentItemGroupProps {
    dataGroupName: string;
    documentGroup: Models.AttributeGroup;
    match: Models.Match;
    siteID?: string;
}

export default function DocumentItemGroup(props: IDocumentItemGroupProps) {
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [reasonForDelete, setReasonForDelete] = useState<string>("");
    const [deleteDocumentMeta, setdeleteDocumentMeta] =
        useState<Models.DocumentMeta>(undefined);
    const [deleteFileName, setDeleteFileName] = useState<string>("");
    const [filesReadySendState, setFilesReadySendState] = useState<
        Models.DocumentLinkUpload[]
    >([]);

    const user: Models.User = userStore((s) => s.user);
    const marketAreaInfo: Models.MarketAreaSettings = useMarketAreaStore(
        (s) => s.marketAreaSettings
    );
    const deleteDocumentMutation = useSendDeleteDocumentRequest();
    const removeDocumentAttributeGroup = filterItemsStore(
        (s) => s.removeDocumentAttributeGroup
    );

    const { match } = props;
    const rows: Models.GroupedAttributeRow[] = props.documentGroup
        ? createChildRows(props.documentGroup.rows)
        : [];
    const headers: string[] = props.documentGroup
        ? props.documentGroup.headers
        : [];
    const name: string = props.documentGroup ? props.documentGroup.name : "";
    const siteId: string = props.documentGroup
        ? props.documentGroup.siteId
        : "";
    const dataGridContent: Models.DataGridContent = withExpander(
        withRemover(getItemGroupData(headers, rows), (documentMeta, fileName) =>
            onDocumentDelete(documentMeta, fileName)
        )
    );

    const allowRemove: boolean = !deleteDocumentMeta
        ? false
        : canRemoveFile(deleteDocumentMeta, user, marketAreaInfo.userMode);

    const confirmHeader: string = allowRemove ? "Delete confirmation" : "Oops!";
    const confirmText: JSX.Element = allowRemove ? (
        <span>
            Are you sure you want to remove the file "
            <strong>{deleteFileName}</strong>"?
        </span>
    ) : (
        <span>
            You are not allowed to remove the file.
            <br />
            Please contact the admin for deleting the file: "
            <strong>{deleteFileName}</strong>"
        </span>
    );

    function updateFileReadySendState(document: Models.DocumentLinkUpload) {
        const updatedFilesReadySend: Models.DocumentLinkUpload[] =
            filesReadySendState.map((fileReadySend) =>
                fileReadySend.file.name === document.file.name
                    ? document
                    : fileReadySend
            );
        setFilesReadySendState(updatedFilesReadySend);
    }

    function addReadySendFile(document: Models.DocumentLinkUpload) {
        setFilesReadySendState((prev) => [...prev, document]);
    }

    function removeReadySendFile(fileName: string) {
        let newFiles: Models.DocumentLinkUpload[] = [];
        if (fileName !== "allFilesFromList") {
            newFiles = filesReadySendState.filter(
                (confirmFile: Models.DocumentLinkUpload) => {
                    return (
                        `${confirmFile.fileName}.${confirmFile.filetype}` !==
                        fileName
                    );
                }
            );
        }
        setFilesReadySendState(newFiles);
    }

    function toggleDeleteModal(
        state: boolean,
        documentMeta?: Models.DocumentMeta,
        fileName?: string
    ): void {
        setModalOpen(state);
        setdeleteDocumentMeta(state ? documentMeta : undefined);
        setDeleteFileName(state ? fileName : "");
    }

    // function updateReason(e: any): void {
    // setReasonForDelete(e.target.value)
    // }

    function onDocumentDelete(
        documentMeta: Models.DocumentMeta,
        fileName: string
    ): void {
        toggleDeleteModal(true, documentMeta, fileName);
    }

    const confirmFileRemove: Function = (e) => {
        e.stopPropagation();

        var doc: Models.AttributeRow = props.documentGroup.rows.find(
            (f) =>
                f.items[1].url.additionalInfo === deleteDocumentMeta.documentId
        );

        const document: Models.DocumentLink = {
            fileName: doc.items[1].url.description,
            type: "",
            url: "",
            reasonForDelete: reasonForDelete,
            pending: false,
            metaData: {
                hostId: doc.items[1].url.id,
                documentId: deleteDocumentMeta.documentId,
            } as Models.DocumentMeta,
        };

        handleFileRemove(document, props.documentGroup, siteId);
        toggleDeleteModal(false);
    };

    function handleFileRemove(
        link: Models.DocumentLink,
        documentGroup: Models.AttributeGroup,
        siteId: string
    ) {
        deleteDocumentMutation.mutate(
            { siteId, fileName: link.fileName },
            {
                onSuccess: () => {
                    removeReadySendFile(link.fileName);
                    const remainingRows = documentGroup.rows.filter((i) => {
                        return (
                            i.items[0].additionalInfo !==
                            link.metaData.documentId
                        );
                    });
                    const newDocGroups = {
                        ...documentGroup,
                        rows: [...remainingRows],
                    };
                    removeDocumentAttributeGroup([newDocGroups]);
                    toast.success("Document deleted successfully");
                },
                onError: () => {
                    toast.error("Could not delete Document");
                },
            }
        );
    }

    const confirmDeleteDisabled: boolean = !allowRemove;

    const modalContent: JSX.Element = (
        <div>
            <h2 className="Modal__header">{confirmHeader}</h2>
            <p className="Modal__description">{confirmText}</p>

            {/* { !allowRemove &&
                    <div className="form-group">
                        <label className="required">Reason for delete request</label>
                        <input type="text" onChange={(e) => this.updateReason(e)} className="form-control Modal__formControl" />
                    </div>
                } */}

            <div className="Modal__buttonGroup">
                <button
                    className="btn btn-secondary"
                    onClick={(e) => toggleDeleteModal(false)}
                >
                    Cancel
                </button>
                {allowRemove && (
                    <button
                        className="btn btn-primary float-end"
                        disabled={confirmDeleteDisabled}
                        onClick={(e) => confirmFileRemove(e)}
                    >
                        Confirm
                    </button>
                )}
            </div>
        </div>
    );

    return (
        <div className="ItemGroup ItemGroup--documents">
            <h3 className="ItemGroup__title">
                <div>
                    <span>{name}</span>
                </div>
            </h3>

            <DocumentDataGrid
                filterable={dataGridContent.data.length > 10}
                isSortable={dataGridContent.data.length > 10}
                showPagination={false}
                content={dataGridContent}
                hideHeader={headers.length === 0}
                // subComponent={this.getSubComponent(this.props)}   grouping is not enabled in documents
            />

            <div className="collapsible-block">
                <CollapsibleCard
                    title="Upload files"
                    collapsed={true}
                    className="card-documents"
                    content={
                        props.siteID === "S-00000" ? (
                            <span>
                                This is a floating asset. Please update the
                                Location ID via Edit Asset before adding a
                                document.
                            </span>
                        ) : (
                            <DocumentsCardFileUpload
                                isUploading={false}
                                isRemoving={deleteDocumentMutation.isPending}
                                match={match}
                                filesReadySendState={filesReadySendState}
                                removeFileFromReadySendState={
                                    removeReadySendFile
                                }
                                addFileToReadySendState={addReadySendFile}
                                updateFileToReadySendState={
                                    updateFileReadySendState
                                }
                            />
                        )
                    }
                />
            </div>

            {modalOpen && (
                <Modal
                    onRequestClose={() => toggleDeleteModal(false)}
                    shouldCloseOnOverlayClick={true}
                    isOpen={modalOpen}
                    contentLabel="Modal"
                    styles={{}}
                    defaultStyles={{}}
                >
                    {modalContent}
                </Modal>
            )}
        </div>
    );
    // }

    // <<-- THIS IS NOT IMPLEMENTED NOW ,CAN BE ADDED LATER IF REQUIRED . CHECK IB-1548 -- >>
    // private getSubComponent(
    //     props: IDocumentItemGroupProps
    // ): (row: any) => JSX.Element {
    //     return (row: any) => {
    //         const isArchive: boolean =
    //             row.original.children &&
    //             row.original.document_id.url.url &&
    //             row.original.document_id.url.url.trim().endsWith(".zip");

    //         if (isArchive) {
    //             return (
    //                 row.original.children && (
    //                     <div
    //                         className={
    //                             "ItemGroup__grouprowsubcomponent archive-tree-view"
    //                         }
    //                     >
    //                         <TreeView data={row.original.children} />
    //                     </div>
    //                 )
    //             );
    //         }

    //         return (
    //             row.original.children && (
    //                 <div className={"ItemGroup__grouprowsubcomponent"}>
    //                     <DataGrid
    //                         filterable={false}
    //                         showPagination={false}
    //                         hideHeader={true}
    //                         content={withRemover(
    //                             getItemGroupData(
    //                                 props.documentGroup.headers,
    //                                 row.original.children
    //                             ),
    //                             (documentId, fileName) =>
    //                                 this.onDocumentDelete(documentId, fileName)
    //                         )}
    //                         isSortable={false}
    //                     />
    //                 </div>
    //             )
    //         );
    //     };
    // }
}
